import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { get } from '../api';
import { Coins } from 'lucide-react';

interface CreditInfo {
  balance: number;
  is_unlimited: boolean;
  member: {
    credit_limit_period: string | null;
    credit_limit_amount: number | null;
    personal_ai_free_quota: number;
    personal_ai_used_this_month: number;
  } | null;
}

// Lightweight credit info cache to avoid fetching on every render
let cachedInfo: CreditInfo | null = null;
let lastFetch = 0;
const CACHE_MS = 30_000; // refresh every 30s

export default function CreditBadge({ compact }: { compact?: boolean }) {
  const [info, setInfo] = useState<CreditInfo | null>(cachedInfo);

  useEffect(() => {
    const now = Date.now();
    if (cachedInfo && now - lastFetch < CACHE_MS) {
      setInfo(cachedInfo);
      return;
    }

    get<CreditInfo>('/credits')
      .then(data => {
        cachedInfo = data;
        lastFetch = Date.now();
        setInfo(data);
      })
      .catch(() => {});
  }, []);

  // Listen for credit updates (dispatched after AI actions)
  useEffect(() => {
    const refresh = () => {
      get<CreditInfo>('/credits')
        .then(data => {
          cachedInfo = data;
          lastFetch = Date.now();
          setInfo(data);
        })
        .catch(() => {});
    };
    window.addEventListener('credits-updated', refresh);
    return () => window.removeEventListener('credits-updated', refresh);
  }, []);

  if (!info) return null;

  const label = info.is_unlimited
    ? '∞'
    : info.balance.toLocaleString('de-DE');

  const freeRemaining = info.member
    ? info.member.personal_ai_free_quota - info.member.personal_ai_used_this_month
    : 0;

  if (compact) {
    return (
      <Link to="/settings/credits" className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-xl bg-warm-50 hover:bg-warm-100 transition-colors">
        <Coins size={14} strokeWidth={1.8} className="text-warm-400" />
        <span className="tabular-nums text-xs font-medium text-warm-600">{label}</span>
        {freeRemaining > 0 && (
          <span className="text-[10px] font-medium px-1.5 py-0.5 bg-teal-50 text-teal-600 rounded-full">{freeRemaining} frei</span>
        )}
      </Link>
    );
  }

  return (
    <Link
      to="/settings/credits"
      className="flex items-center gap-2 px-3 py-2 rounded-lg text-sm text-warm-500 hover:bg-warm-50 hover:text-teal-600 transition-colors"
    >
      <Coins size={16} strokeWidth={1.8} className="text-warm-300" />
      <span className="tabular-nums font-medium">{label}</span>
      <span className="text-xs text-warm-300">Credits</span>
      {freeRemaining > 0 && (
        <span className="text-[10px] px-1.5 py-0.5 bg-teal-50 text-teal-600 rounded-full">{freeRemaining} frei</span>
      )}
    </Link>
  );
}
